import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ReportChart } from '../components/reports/report-chart.js';
import type { RangePickerValue } from '../components/reports/range-picker.js';
import { ReportShell, StatTile } from '../components/reports/report-shell.js';
import { Label, cn } from '@tailoring-crm/ui';
import { useAuth } from '../lib/auth/auth-context.js';
import { useHasModule } from '../lib/billing/entitlements.js';
import { formatCurrency, formatDate, formatMobile, formatNumber } from '../lib/format.js';
import { customerReportCsvPath } from '../lib/reports/reports-api.js';
import { useCustomerReport } from '../lib/reports/use-reports.js';

/**
 * New against returning, who is worth the most, and — the part worth opening the screen for —
 * who has stopped coming.
 *
 * The lapsed list is deliberately the longest section and every row is a tappable phone number,
 * because this report is only useful if it ends in somebody making calls. Sorted by lifetime
 * value: if a shop gets through ten calls on a quiet Tuesday, these are the ten.
 */
const LAPSED_WINDOWS = [30, 60, 90, 180] as const;

export function CustomersReportPage(): ReactElement {
  const { t } = useTranslation();
  const { role } = useAuth();
  const [range, setRange] = useState<RangePickerValue>({ preset: 'last_30_days' });
  const [lapsedDays, setLapsedDays] = useState<number>(90);

  const allowed = role !== 'tailor';
  const inPlan = useHasModule('advanced_analytics');
  const params = { ...range, lapsedDays };
  const report = useCustomerReport(params, { enabled: allowed && inPlan });
  const data = report.data;

  return (
    <ReportShell
      title={t('reports.customers.title')}
      description={t('reports.customers.description')}
      range={range}
      onRangeChange={setRange}
      resolved={data?.range}
      csvPath={customerReportCsvPath(params)}
      isPending={report.isPending && allowed && inPlan}
      allowed={allowed}
      module="advanced_analytics"
      controls={
        <div className="flex flex-col gap-1.5">
          <Label>{t('reports.customers.lapsedWindow')}</Label>
          <div
            role="radiogroup"
            aria-label={t('reports.customers.lapsedWindow')}
            className="flex flex-wrap gap-1.5"
          >
            {LAPSED_WINDOWS.map((days) => (
              <button
                key={days}
                type="button"
                role="radio"
                aria-checked={lapsedDays === days}
                onClick={() => setLapsedDays(days)}
                className={cn(
                  'rounded-full border px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  lapsedDays === days
                    ? 'border-transparent bg-primary text-primary-foreground'
                    : 'border-border hover:bg-accent',
                )}
              >
                {t('reports.customers.dayCount', { count: days })}
              </button>
            ))}
          </div>
        </div>
      }
    >
      {data ? (
        <>
          <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <StatTile
              label={t('reports.customers.new')}
              value={formatNumber(data.totals.newCustomers)}
              hint={formatCurrency(data.totals.newCustomerRevenuePaise)}
            />
            <StatTile
              label={t('reports.customers.returning')}
              value={formatNumber(data.totals.returningCustomers)}
              hint={formatCurrency(data.totals.returningCustomerRevenuePaise)}
            />
            <StatTile
              label={t('reports.customers.active')}
              value={formatNumber(data.totals.activeCustomers)}
            />
            <StatTile
              label={t('reports.customers.revenue')}
              value={formatCurrency(data.totals.revenuePaise)}
            />
          </dl>

          <ReportChart
            title={t('reports.customers.newByDay')}
            labelHeading={t('reports.day')}
            valueHeading={t('reports.customers.new')}
            points={data.byDay.map((row) => ({
              label: row.day,
              value: row.newCustomers,
              display: formatNumber(row.newCustomers),
            }))}
          />

          <section className="flex flex-col gap-2" aria-label={t('reports.customers.top')}>
            <h2 className="text-sm font-semibold">{t('reports.customers.top')}</h2>
            {data.top.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('reports.noData')}</p>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {data.top.map((row) => (
                  <li key={row.customerId}>
                    <Link
                      to={`/customers?q=${encodeURIComponent(row.customerPhone)}`}
                      className="flex items-center justify-between gap-3 rounded-lg border border-border p-3 text-sm hover:bg-accent"
                    >
                      <span className="min-w-0">
                        <span className="block font-medium">{row.customerName}</span>
                        <span className="block text-muted-foreground">
                          {t('reports.customers.orderCount', { count: row.orderCount })}
                        </span>
                      </span>
                      <span className="font-semibold tabular-nums">
                        {formatCurrency(row.revenuePaise)}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="flex flex-col gap-2" aria-label={t('reports.customers.lapsed')}>
            <h2 className="text-sm font-semibold">
              {t('reports.customers.lapsedTitle', { count: data.lapsedDays })}
            </h2>
            {data.lapsed.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('reports.customers.noneLapsed')}</p>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {data.lapsed.map((row) => (
                  <li
                    key={row.customerId}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3 text-sm"
                  >
                    <span className="min-w-0">
                      <span className="block font-medium">{row.customerName}</span>
                      <span className="block text-muted-foreground">
                        {row.lastOrderAt
                          ? t('reports.customers.lastOrder', {
                              date: formatDate(row.lastOrderAt),
                              count: row.daysSinceLastOrder ?? 0,
                            })
                          : t('reports.customers.neverOrdered')}
                      </span>
                    </span>
                    <span className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {formatCurrency(row.lifetimePaise)}
                      </span>
                      <a
                        href={`tel:+91${row.customerPhone}`}
                        className="text-primary underline underline-offset-2"
                      >
                        {formatMobile(row.customerPhone)}
                      </a>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      ) : null}
    </ReportShell>
  );
}

export default CustomersReportPage;
